export const UNTITLED_EXPLORE_TITLE = "Untitled exploration";
const EXPLORE_TITLE_PREFIX = "Exploring ";

interface ExploreDefaultTitleParams {
  datasetName: string | undefined;
  sourceCellLabel: string | undefined | null;
  sourceHexTitle: string | undefined;
}

export function getExploreDefaultTitle({
  datasetName,
  sourceCellLabel,
  sourceHexTitle,
}: ExploreDefaultTitleParams): string {
  const exploringFrom = sourceCellLabel ?? sourceHexTitle ?? datasetName;
  if (!exploringFrom) {
    return UNTITLED_EXPLORE_TITLE;
  } else {
    return `${EXPLORE_TITLE_PREFIX}${exploringFrom === datasetName ? toSentenceCase(exploringFrom) : exploringFrom}`;
  }
}

export function isCustomExploreTitle(title: string): boolean {
  return (
    title.trim().length > 0 &&
    !title.startsWith(EXPLORE_TITLE_PREFIX) &&
    title !== UNTITLED_EXPLORE_TITLE
  );
}

/**
 * Converts a string to sentence case.
 * Handles underscore, dash, and camelCase formats.
 * Preserves common acronyms like "ID".
 *
 * @param str The input string to convert
 * @returns The sentence case string
 */
function toSentenceCase(str: string): string {
  if (!str) return str;

  // Special words that should remain uppercase
  const specialWords = ["id", "url", "api", "sdk", "ui", "ux", "sso"];

  // Insert spaces at transition points
  const spacedString = str
    // Add space between lowercase and uppercase/number
    .replace(/([a-z])([A-Z0-9])/g, "$1 $2")
    // Add space between number and uppercase
    .replace(/([0-9])([A-Z])/g, "$1 $2")
    // Add space between letters and numbers (regardless of case)
    .replace(/([a-zA-Z])([0-9])/g, "$1 $2");

  // Split by underscores, dashes, and spaces
  const words = spacedString.split(/[_\- ]+/).filter(Boolean);

  if (words.length === 0) return "";

  // Process each word
  const processedWords = words.map((word, index) => {
    const lowercaseWord = word.toLowerCase();

    // Check if this word should remain uppercase
    if (specialWords.includes(lowercaseWord)) {
      return lowercaseWord.toUpperCase();
    }

    // Capitalize first word, lowercase the rest
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    } else {
      return word.toLowerCase();
    }
  });

  // Join with spaces
  return processedWords.join(" ");
}
